// Enemy torpedoes: the destroyers' teeth. Long Lance spreads run just awash
// with a phosphorescent wake trail; the lookouts sing out when the wakes get
// inside ~2.5 km, and after that it's the helm's problem — turn INTO the
// spread and comb the tracks, or eat one below the belt where the armor
// isn't. No countermeasures, exactly like the real thing.

import * as THREE from "three";

const TORP_SPEED = 34;   // m/s — fast-setting Long Lance, tuned for gameplay
const RUN_TIME = 300;    // seconds before a fish runs out of steam

export class Torpedoes {
  constructor(G) {
    this.G = G;
    this.fish = [];
    this._warnCool = 0;
  }

  // A spread of `n` fish from (x, z), fanned around `heading`.
  launch(x, z, heading, n = 4, fan = 0.045) {
    for (let i = 0; i < n; i++) {
      const h = heading + (i - (n - 1) / 2) * fan;
      this.fish.push({
        x, z, h, t: 0,
        wakeAcc: Math.random() * 0.1,
        warned: false,
      });
    }
  }

  update(dt) {
    const G = this.G;
    this._warnCool = Math.max(0, this._warnCool - dt);
    const p = G.shipWorldPos();
    const s = G.shipState;

    for (let i = this.fish.length - 1; i >= 0; i--) {
      const f = this.fish[i];
      f.t += dt;
      if (f.t > RUN_TIME) { this.fish.splice(i, 1); continue; }
      f.x += Math.sin(f.h) * TORP_SPEED * dt;
      f.z += Math.cos(f.h) * TORP_SPEED * dt;

      // wake trail: churned foam every few meters, long-lived so the track
      // reads as a line on the water — the lookout's only warning, so it
      // has to be spottable from the bridge (and it glows faintly at night)
      f.wakeAcc += dt;
      if (f.wakeAcc > 0.085) {
        f.wakeAcc = 0;
        G.worldFx.spawn(new THREE.Vector3(f.x, 0.8, f.z), {
          count: 1, speed: 0.7, life: 9, size: 2.6, jitter: 0.9,
          colors: [0xdfeaf2, 0xc9dce8, 0xeef6fb], gravity: -0.1, drag: 0.6, grow: 1.5,
        });
      }

      const dx = f.x - p.x, dz = f.z - p.z;
      const dist = Math.hypot(dx, dz);

      // lookouts spot the wakes coming in
      if (!f.warned && dist < 2500) {
        f.warned = true;
        if (this._warnCool <= 0) {
          this._warnCool = 8;
          const brg = Math.atan2(dx, dz);
          const rel = Math.atan2(Math.sin(brg - s.heading), Math.cos(brg - s.heading));
          const side = rel > 0 ? "STARBOARD" : "PORT";
          const quarter = Math.abs(rel) < Math.PI / 3 ? "BOW" : Math.abs(rel) > 2 * Math.PI / 3 ? "QUARTER" : "BEAM";
          G.hud.log(`TORPEDO WAKES ${side} ${quarter} — COMB THE TRACKS! (turn into them)`, "bad");
          G.audio.klaxon(2);
          if (!G.gq) G.soundGQ(true, true);
        }
      }

      // hit test in ship-local frame (the hull is 270 m of target)
      if (dist < 320) {
        const local = G.shipInner.worldToLocal(new THREE.Vector3(f.x, 0, f.z));
        if (local.x > 2 && local.x < 270 && Math.abs(local.z - 17.5) < 17.5) {
          this.fish.splice(i, 1);
          this.detonate(f, local);
          continue;
        }
      }
    }
  }

  detonate(f, local) {
    const G = this.G;
    const at = G.shipInner.localToWorld(new THREE.Vector3(local.x, 0, local.z));
    // a torpedo column dwarfs a shell splash
    G.worldFx.spawn(at, {
      count: 70, dir: new THREE.Vector3(0, 1, 0), speed: 34, life: 3.2, size: 7, jitter: 6,
      colors: [0xeef4f8, 0xcfe0ea, 0xa8c0d0], gravity: -9, drag: 0.35,
    });
    G.worldFx.spawn(at, {
      count: 24, speed: 16, life: 0.5, size: 8, jitter: 3,
      colors: [0xffd890, 0xff8a3c], drag: 2,
    });
    G.audio.hitTaken();
    G.audio.splash(true);
    G.shake(2);
    G.damage.torpedoHit(local.x);
  }
}
